<template>
  <div :class="{ mobile: isMobile }">
    <v-btn v-if="!isHideButton" color="primary" @click="setGalleryOpen(true)">
      {{ $t('gallery.buttonText') }}
    </v-btn>

    <v-dialog
      v-model="dialogVisible"
      max-width="800"
      persistent
      :fullscreen="isLessThen750"
    >
      <v-overlay :model-value="loading" scrim="transparent">
        <v-progress-circular
          color="primary"
          indeterminate
          size="64"
        ></v-progress-circular>
      </v-overlay>
      <v-card class="main-card">
        <v-card-title class="d-flex justify-space-between">
          <div>
            <v-icon @click="closeDialog" class="close-btn me-7">
              <my-svg
                icon="close-square"
                :svg-max-width="34"
                :svg-max-height="34"
              />
            </v-icon>
            <span class="text-black">
              {{ hideTitle ? '' : $t('gallery.title') }}
            </span>
          </div>
          <v-spacer />
          <v-spacer />
          <v-text-field
            v-model="searchQuery"
            :label="$t('gallery.search')"
            single-line
            hide-details
            class="input-search"
            variant="solo"
            :class="{ 'left-btn': rtl, 'right-btn': !rtl }"
          >
            <template #append-inner>
              <my-svg icon="search" />
            </template>
          </v-text-field>
        </v-card-title>

        <v-row v-if="!isApprovedUpload">
          <v-col cols="12">
            <h1 class="text-center red--text">לא ניתן להעלות קבצים מהטלפון</h1>
          </v-col>
        </v-row>
        <div v-if="maxBytes" class="storage-meter px-4">
          <div class="d-flex justify-space-between text-caption mb-1">
            <span>
              {{
                $t('gallery.storageUsage', {
                  used: usedMb,
                  max: maxMb,
                })
              }}
            </span>
          </div>
          <v-progress-linear
            :model-value="storagePercent"
            :color="storagePercent >= 100 ? 'error' : 'primary'"
            height="8"
            rounded
          />
        </div>
        <v-card-text class="all-media my-scrollbar">
          <v-row>
            <v-col
              v-for="(media, index) in mediaStore"
              :key="index"
              cols="12"
              sm="6"
              md="4"
            >
              <v-card
                v-if="media?.url && media?.type"
                @click="selectMedia(media)"
                :class="{ 'selected-media': chosenMedia === media }"
                class="media-card"
              >
                <my-media
                  :mediaType="media.type"
                  :media="media.url"
                  :snapshot="true"
                >
                  <div class="media-title rounded-lg">
                    <v-text-field
                      @click.stop="console.log(media)"
                      :ref="`titleInput-${media._id}`"
                      v-model="editingTitle"
                      v-if="currentTitleEdit === media._id"
                      density="compact"
                      variant="outlined"
                      hide-details
                    />
                    <span v-else>{{ media.title }}</span>
                  </div>
                  <div
                    style="position: absolute; top: 8px"
                    class="edit d-flex"
                    :class="{ 'left-icon': rtl, 'right-icon': !rtl }"
                  >
                    <template v-if="currentTitleEdit === media._id">
                      <v-icon
                        @click.stop="saveTitleEdit(media)"
                        class="edit-icon approve-icon"
                        :disabled="loading"
                        color="success"
                      >
                        mdi-check-circle
                      </v-icon>
                      <v-icon
                        @click.stop="cancelTitleEdit(media)"
                        class="edit-icon cancel-icon"
                        :disabled="loading"
                        color="error"
                      >
                        mdi-close-circle
                      </v-icon>
                    </template>
                    <template v-else>
                      <v-icon
                        @click.stop="editTitleHandler(media)"
                        class="edit-icon soft-background me-1"
                        :disabled="loading"
                      >
                        mdi-pencil
                      </v-icon>
                      <v-icon
                        v-if="media.type === 'image' && isAIEnabled"
                        @click.stop="openAIDialog('edit', media)"
                        class="edit-icon soft-background me-1"
                        :disabled="loading"
                        size="18"
                      >
                        mdi-auto-fix
                      </v-icon>
                      <v-icon
                        @click.stop="deleteHandler(media)"
                        class="delete-icon soft-background"
                        :disabled="loading"
                      >
                        mdi-delete
                      </v-icon>
                    </template>
                  </div>

                  <v-icon
                    class="open-dialog-icon soft-background"
                    :class="{ 'left-icon': !rtl, 'right-icon': rtl }"
                    color="gray"
                    @click.stop="openPreviewDialog(media)"
                  >
                    mdi-eye
                  </v-icon>
                </my-media>
              </v-card>
            </v-col>
          </v-row>
          <v-pagination
            v-if="mediaCount > perPage"
            class="mt-6"
            v-model="currentPage"
            :total-visible="perPage"
            :length="Math.ceil(mediaCount / perPage)"
            color="primary"
          />
        </v-card-text>
        <v-card-actions class="actions-container">
          <span class="rightProportions">
            {{ $t('gallery.rightProportions') }}
          </span>
          <v-btn
            :disabled="!isApprovedUpload"
            class="upload-btn"
            @click="openInput"
          >
            <div class="d-flex flex-column align-center w-100">
              <div class="d-flex align-center">
                <span class="me-4 upload-text">{{ $t('gallery.upload') }}</span>
                <my-svg icon="upload" />
              </div>
              <span v-if="!isMobile" class="paste-hint-text">{{
                $t('gallery.pasteHint')
              }}</span>
            </div>
          </v-btn>
          <v-btn
            v-if="isMobile && isApprovedUpload"
            class="paste-btn"
            @click="pasteFromClipboard"
            :disabled="loading"
          >
            <div class="d-flex align-center">
              <span class="me-4 upload-text">{{
                $t('gallery.pasteFromClipboard')
              }}</span>
              <v-icon>mdi-clipboard-outline</v-icon>
            </div>
          </v-btn>
          <FeatureBadge selector=".ai-btn">
            <v-btn
              v-if="isAIEnabled"
              class="ai-btn mb-2"
              @click="openAIDialog('generate')"
              :disabled="loading"
            >
              <span class="me-4 upload-text">
                {{ $t('gallery.aiGenerate') }}
              </span>
              <v-icon>mdi-auto-fix</v-icon>
            </v-btn>
          </FeatureBadge>
          <v-btn
            v-if="chosenMedia"
            class="choose-btn ms-0"
            @click="closeAndChoose"
          >
            <span class="upload-text">
              {{ $t('gallery.choose') }}
            </span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <MediaPreviewDialog
      v-model="previewDialogVisible"
      v-if="selectedMedia"
      :media="selectedMedia"
      @close="closePreviewDialog"
    />
    <AIGenerateImageDialog
      v-model="aiDialogVisible"
      :mode="aiDialogMode"
      :media="aiDialogMedia"
      @generated="() => uploaded(false)"
      @approved="handleAIApproved"
    />
    <v-snackbar v-model="quotaSnackbar" :timeout="4000">
      {{ $t('gallery.storageExceeded') }}
      <template v-slot:actions="props">
        <v-btn
          color="blue"
          variant="text"
          v-bind="props"
          @click="quotaSnackbar = false"
        >
          {{ $t('gallery.close') }}
        </v-btn>
      </template>
    </v-snackbar>
    <v-snackbar v-model="badMediaSnackbar" :timeout="1500">
      {{ $t('gallery.badImage') }}
      <template v-slot:actions="props">
        <v-btn
          color="blue"
          variant="text"
          v-bind="props"
          @click="badMediaSnackbar = false"
        >
          {{ $t('gallery.close') }}
        </v-btn>
      </template>
    </v-snackbar>
    <v-snackbar v-model="videoTooLargeSnackbar" :timeout="2500">
      {{ $t('gallery.videoTooLarge') }}
      <template v-slot:actions="props">
        <v-btn
          color="blue"
          variant="text"
          v-bind="props"
          @click="videoTooLargeSnackbar = false"
        >
          {{ $t('gallery.close') }}
        </v-btn>
      </template>
    </v-snackbar>
    <v-snackbar v-model="videoPartnerSnackbar" :timeout="2500">
      {{ $t('gallery.videoPartnerOnly') }}
      <template v-slot:actions="props">
        <v-btn
          color="blue"
          variant="text"
          v-bind="props"
          @click="videoPartnerSnackbar = false"
        >
          {{ $t('gallery.close') }}
        </v-btn>
      </template>
    </v-snackbar>
    <v-snackbar v-model="videoTypeSnackbar" :timeout="2500">
      {{ $t('gallery.videoMp4Only') }}
      <template v-slot:actions="props">
        <v-btn
          color="blue"
          variant="text"
          v-bind="props"
          @click="videoTypeSnackbar = false"
        >
          {{ $t('gallery.close') }}
        </v-btn>
      </template>
    </v-snackbar>
    <v-snackbar v-model="noClipboardImageSnackbar" :timeout="1500">
      {{ $t('gallery.noClipboardImage') }}
      <template v-slot:actions="props">
        <v-btn
          color="blue"
          variant="text"
          v-bind="props"
          @click="noClipboardImageSnackbar = false"
        >
          {{ $t('gallery.close') }}
        </v-btn>
      </template>
    </v-snackbar>
    <upload-media
      v-model="mediaUploadVisible"
      v-if="mediaUploadVisible"
      @uploaded="() => uploaded(true)"
      :imageFromInput="media"
    />
    <v-file-input
      v-show="false"
      v-model="selectedFile"
      :accept="acceptFiles"
      @update:model-value="uploadMediaFile"
      class="custom-file-input"
      ref="fileInput"
    />
  </div>
</template>
<script lang="ts" setup>
import localforage from 'localforage'
import { useSwal } from '@/composables/useSwal'
import { useDisplay } from 'vuetify'
import { useMediaStore } from '@/store/media'
import { useMobile } from '@/composables/useMobile'
import MediaPreviewDialog from './MediaPreviewDialog.vue'
import AIGenerateImageDialog from './dialogs/AIGenerateImageDialog.vue'
import FeatureBadge from '@/components/badges/FeatureBadge.vue'
import { isDesktopDevice } from '@/utils/checker'
import { useAuthStore } from '@/store'
import {
  exceedsMp4Limit,
  isMp4File,
  isVideoFile,
  videoUploadAllowed,
} from '@/helpers/mp4Upload'

const mediaStoreApi = useMediaStore()
const {
  isHideButton,
  isOnlyImage,
  getMediaStore: mediaStore,
  getMediaCount: mediaCount,
  getPerPage: perPage,
  current,
  mediaGallery,
  totalBytes,
  maxBytes,
} = storeToRefs(mediaStoreApi)

const {
  deleteMediaFromServer,
  getMediaUsage,
  updateMediaTitleOnServer,
  setGalleryOpen,
  indexMedia,
  chosenCallback,
} = useMediaStore()

const { createSwal } = useSwal()
const { t } = useI18n()
const vuetifyDisplay = useDisplay()
const { isRtl } = useLocale()

// state
const media = ref<Media | null>(null)
const selectedFile = ref<File | null>(null)
const badMediaSnackbar = ref(false)
const quotaSnackbar = ref(false)
const noClipboardImageSnackbar = ref(false)
const videoTooLargeSnackbar = ref(false)
const videoPartnerSnackbar = ref(false)
const videoTypeSnackbar = ref(false)

const selectedMedia = ref<Media | null>(null)
const mediaUploadVisible = ref(false)
const currentPage = ref<number>(1)
const LOCAL_CURRENT_PAGE_KEY = 'media_current_page'
const loading = ref(false)
const searchQuery = ref<string>('')
const chosenMedia = ref<Media | null>(null)
const previewDialogVisible = ref(false)
const aiDialogVisible = ref(false)
const aiDialogMode = ref<'generate' | 'edit'>('generate')
const aiDialogMedia = ref<Media | null>(null)
const currentTitleEdit = ref<string | null>(null)
const editingTitle = ref<string>('')
const originalTitle = ref<string>('')

const fileInput = ref<HTMLInputElement | null>(null)

const authStore = useAuthStore()
const { isAIEnabled, isInstitution } = storeToRefs(authStore)
const canUploadVideo = computed(() =>
  authStore.hasPartnerFeature('video_upload')
)

const hideTitle = computed(() => vuetifyDisplay.width.value < 900)
const acceptFiles = computed(() => {
  if (onlyImage.value) return 'image/*'
  const imagesAndAudio = '.jpg,.jpeg,.png,.mp3'
  return canUploadVideo.value
    ? `video/mp4,.mp4,${imagesAndAudio}`
    : imagesAndAudio
})

const onlyImage = computed(() => Boolean(isOnlyImage.value))
const rtl = computed(() => isRtl.value)
const isLessThen750 = computed(() => vuetifyDisplay.height.value < 750)
const dialogVisible = computed<boolean>({
  get: () => mediaGallery.value.open,
  set: (val: boolean) => setGalleryOpen(val),
})

const { isMobile } = useMobile()

const usedMb = computed(() => (totalBytes.value / (1024 * 1024)).toFixed(1))
const maxMb = computed(() =>
  maxBytes.value ? (maxBytes.value / (1024 * 1024)).toFixed(0) : '30'
)
const storagePercent = computed(() => {
  if (!maxBytes.value) return 0
  return Math.min(100, (totalBytes.value / maxBytes.value) * 100)
})

function exceedsQuota(file: File | null | undefined) {
  if (!file || maxBytes.value == null) return false
  if (file.size > maxBytes.value) return true
  return totalBytes.value + file.size > maxBytes.value
}

// methods
async function deleteHandler(mediaItem: Media) {
  try {
    if (!mediaItem._id) return (loading.value = false)

    loading.value = true
    const usedInGames = await getMediaUsage(mediaItem._id)
    loading.value = false

    const gameNames = usedInGames.map(g => g.name).filter(Boolean)
    const { isConfirmed } = await createSwal({
      icon: 'warning',
      title: t('gallery.deleteConfirm'),
      text: gameNames.length
        ? t('gallery.deleteUsedWarning', { games: gameNames.join(', ') })
        : '',
    })
    if (!isConfirmed) return
    if (chosenMedia.value?._id === mediaItem._id) chosenMedia.value = null
    loading.value = true
    await deleteMediaFromServer(mediaItem._id, {
      removeFromGames: true,
      mediaUrl: mediaItem.url,
    })
    if (current.value && current.value === mediaItem.url) {
      chosenCallback({
        src: '',
        format: '',
        url: '',
      })
    }
  } finally {
    loading.value = false
  }
}

const isApprovedUpload = computed(
  () =>
    isDesktopDevice(navigator) ||
    (!isDesktopDevice(navigator) && !isInstitution)
)

function editTitleHandler(mediaItem: Media) {
  if (loading.value) return
  if (currentTitleEdit.value === mediaItem._id) {
    saveTitleEdit(mediaItem)
    return
  }
  originalTitle.value = mediaItem.title || ''
  editingTitle.value = mediaItem.title || ''
  currentTitleEdit.value = mediaItem._id || null
  nextTick(() => {
    const inputRef = document.querySelector<HTMLInputElement>(
      `[ref="titleInput-${mediaItem._id}"]`
    )
    if (inputRef) inputRef.focus()
  })
}

async function saveTitleEdit(mediaItem: Media) {
  try {
    if (!editingTitle.value.trim()) {
      editingTitle.value = originalTitle.value
      currentTitleEdit.value = null
      return
    }
    if (mediaItem?._id && editingTitle.value !== originalTitle.value) {
      loading.value = true
      await updateMediaTitleOnServer({
        mediaId: mediaItem?._id,
        newTitle: editingTitle.value.trim(),
      })
      loading.value = false
      mediaItem.title = editingTitle.value.trim()
    }
    currentTitleEdit.value = null
    editingTitle.value = ''
    originalTitle.value = ''
  } catch (error) {
    mediaItem.title = originalTitle.value
    currentTitleEdit.value = null
    editingTitle.value = ''
    originalTitle.value = ''
    throw error
  }
}

function cancelTitleEdit(mediaItem: Media) {
  mediaItem.title = originalTitle.value
  currentTitleEdit.value = null
  editingTitle.value = ''
  originalTitle.value = ''
}

function uploaded(isAlreadyCropped = false) {
  chosenMedia.value = mediaStore.value[0]
  closeAndChoose(isAlreadyCropped)
}

function openInput() {
  const root = fileInput.value as {
    $el?: HTMLElement
    click?: () => void
  } | null
  const input = root?.$el?.querySelector('input')
  if (input) input.value = ''
  root?.click?.()
}

function handlePaste(e: ClipboardEvent) {
  if (!dialogVisible.value || !isApprovedUpload.value) return
  const items = e.clipboardData?.items
  if (!items) return
  for (const item of Array.from(items)) {
    if (item.type.startsWith('image/')) {
      const file = item.getAsFile()
      if (!file) break
      if (exceedsQuota(file)) {
        quotaSnackbar.value = true
        break
      }
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        media.value = {
          url: reader.result as string,
          name: `pasted-image.${file.type.split('/')[1] || 'png'}`,
          file,
        }
      }
      break
    }
  }
}

async function pasteFromClipboard() {
  if (!isApprovedUpload.value) return
  try {
    const clipboardItems = await navigator.clipboard.read()
    for (const clipboardItem of clipboardItems) {
      for (const type of clipboardItem.types) {
        if (type.startsWith('image/')) {
          const blob = await clipboardItem.getType(type)
          const ext = type.split('/')[1] || 'png'
          const file = new File([blob], `pasted-image.${ext}`, { type })
          if (exceedsQuota(file)) {
            quotaSnackbar.value = true
            return
          }
          const reader = new FileReader()
          reader.readAsDataURL(file)
          reader.onload = () => {
            media.value = {
              url: reader.result as string,
              name: file.name,
              file,
            }
          }
          return
        }
      }
    }
    noClipboardImageSnackbar.value = true
  } catch {
    noClipboardImageSnackbar.value = true
  }
}

function selectedUploadFile(value?: File | File[] | null) {
  const selected = value ?? (selectedFile.value as File | File[] | null)
  if (!selected) return null
  return Array.isArray(selected) ? selected[0] || null : selected
}

function revokePreview(value: { url?: string } | null) {
  if (value?.url?.startsWith('blob:')) URL.revokeObjectURL(value.url)
}

async function uploadMediaFile(value?: File | File[] | null) {
  const file = selectedUploadFile(value)
  if (!file) return
  if (onlyImage.value && file.type.split('/')[0] !== 'image') return
  if (!videoUploadAllowed(file, canUploadVideo.value)) {
    videoPartnerSnackbar.value = true
    selectedFile.value = null
    return
  }
  if (isVideoFile(file) && !isMp4File(file)) {
    videoTypeSnackbar.value = true
    selectedFile.value = null
    return
  }
  if (exceedsMp4Limit(file)) {
    videoTooLargeSnackbar.value = true
    selectedFile.value = null
    return
  }
  if (exceedsQuota(file)) {
    quotaSnackbar.value = true
    selectedFile.value = null
    return
  }
  if (isMp4File(file)) {
    revokePreview(media.value)
    media.value = {
      url: URL.createObjectURL(file),
      name: file.name,
      file,
      type: 'video/mp4',
      mime: 'video/mp4',
    }
    return
  }
  const reader = new FileReader()
  reader.readAsDataURL(file)
  reader.onload = () => {
    media.value = {
      url: reader.result as string,
      name: file.name,
      file,
    }
  }
}

async function handleAIApproved({ url, title }) {
  loading.value = true
  try {
    const response = await fetch(url)
    const blob = await response.blob()
    const type = blob.type || 'image/jpeg'
    const ext = type.split('/')[1] || 'jpg'

    media.value = {
      url: url,
      name: title || `ai-generated-${Date.now()}.${ext}`,
      file: new File([blob], title || `ai-generated-${Date.now()}.${ext}`, {
        type: type,
      }),
      type: type,
      isAI: true,
    }
  } catch (e) {
    console.error('Failed to fetch AI image for cropping:', e)
  } finally {
    loading.value = false
  }
}

async function closeAndChoose(isAlreadyCropped = false) {
  if (
    !isAlreadyCropped &&
    chosenMedia.value?.type === 'image' &&
    !chosenMedia.value.url.startsWith('data:')
  ) {
    loading.value = true
    try {
      const response = await fetch(chosenMedia.value.url)
      const blob = await response.blob()
      media.value = {
        url: chosenMedia.value.url,
        name: chosenMedia.value.title,
        file: new File([blob], chosenMedia.value.title || 'image.jpg', {
          type: blob.type,
        }),
        type: blob.type,
      }
      return
    } catch (e) {
      console.error('Failed to fetch image for cropping:', e)
    } finally {
      loading.value = false
    }
  }

  const folder = import.meta.env.VITE_APP_CLOUD_FOLDER as string
  const url = chosenMedia.value?.url || ''
  const fileName = url.split(folder)[1]
  if (!fileName) {
    badMediaSnackbar.value = true
    return
  }
  const [src, format] = fileName.split('.')
  chosenCallback({
    src: `assets${fileName}`,
    format,
    url,
  })
  chosenMedia.value = null
  setGalleryOpen(false)
}

function selectMedia(mediaItem: Media) {
  chosenMedia.value = chosenMedia.value === mediaItem ? null : mediaItem
}

async function loadData() {
  loading.value = true
  await indexMedia({
    page: currentPage.value,
    perPage: perPage.value,
    search: searchQuery.value,
    type: onlyImage.value ? 'image' : '',
  })
  // await sleep(1000)
  loading.value = false
}

function closeDialog() {
  chosenMedia.value = null
  setGalleryOpen(false)
}

function openPreviewDialog(mediaItem: Media) {
  selectedMedia.value = mediaItem
  previewDialogVisible.value = true
}

function closePreviewDialog() {
  previewDialogVisible.value = false
}

function openAIDialog(
  mode: 'generate' | 'edit',
  mediaItem: Media | null = null
) {
  aiDialogMode.value = mode
  aiDialogMedia.value = mediaItem
  aiDialogVisible.value = true
}

function onEsc(e: KeyboardEvent) {
  if (e.key !== 'Escape') return
  if (previewDialogVisible.value) previewDialogVisible.value = false
  else if (mediaUploadVisible.value) mediaUploadVisible.value = false
  else if (dialogVisible.value) dialogVisible.value = false
}

watch(currentPage, async val => {
  await loadData()
  // Only save if currentPage is within valid range
  const maxPage = Math.ceil(mediaCount.value / perPage.value)
  if (val > 0 && val <= maxPage) {
    window.localStorage.setItem(LOCAL_CURRENT_PAGE_KEY, String(val))
  }
})
watch(searchQuery, () => {
  currentPage.value = 1
  loadData()
})
watch(media, (val, oldVal) => {
  if (val && !oldVal) mediaUploadVisible.value = true
})
watch(mediaUploadVisible, val => {
  if (!val) {
    revokePreview(media.value)
    media.value = null
    selectedFile.value = null
  }
})

// lifecycle
onMounted(async () => {
  window.addEventListener('keyup', onEsc)
  window.addEventListener('paste', handlePaste)
  const urlParams = new URLSearchParams(window.location.search)
  const fileKey = urlParams.get('file_key')
  if (fileKey) {
    const m = await localforage.getItem<Media>(fileKey)
    if (m) {
      const storedFile = m.file
      const storedIsVideo =
        (storedFile && isVideoFile(storedFile)) ||
        String(m.mime || m.type || '').startsWith('video/')
      if (storedIsVideo && !canUploadVideo.value) {
        videoPartnerSnackbar.value = true
      } else {
        dialogVisible.value = true
        media.value = m
        mediaUploadVisible.value = true
      }
      await localforage.removeItem(fileKey)
    }
    urlParams.delete('file_key')
    const newUrl = `${window.location.pathname}?${urlParams.toString()}`
    window.history.replaceState({}, '', newUrl)
  }
  // restore saved current page if available (from localStorage)
  const raw = window.localStorage.getItem(LOCAL_CURRENT_PAGE_KEY)
  const savedPage = parseInt(raw ?? '', 10)
  if (Number.isInteger(savedPage) && savedPage > 0) {
    currentPage.value = savedPage
  }
  await loadData()
})
onBeforeUnmount(() => {
  window.removeEventListener('keyup', onEsc)
  window.removeEventListener('paste', handlePaste)
})
</script>

<style scoped lang="scss">
.v-card-title {
  font-size: 16px;
  font-weight: 500;
  color: white;
  padding: 16px;
}

.v-card-actions {
  padding: 8px;
}

.v-dialog .v-card.main-card {
  margin-bottom: 0;
  border: 3px solid transparent;
  /*2*/
  background:
    linear-gradient(#ffffff 0 0) padding-box,
    var(--linearHotPinkDodgerBlue) border-box;
  display: inline-block;
  border-radius: 13px;
}

:deep(.v-dialog) {
  /* min-height: 600px !important; */
  overflow-y: visible !important;
  border-radius: 13px;
  box-shadow: none;
}

.media-title {
  width: 100%;
  display: inline;
  font-size: 16px;
  font-weight: 500;
  padding: 8px;
}

.selected-media {
  border: 3px solid #00bcd4;
}

.open-dialog-icon {
  font-size: 24px;
  cursor: pointer;
}

.input-search {
  border-radius: 20px;
  top: 20px;
}

:deep(.v-input__control),
:deep(.v-text-field .v-field) {
  background-color: rgba(240, 248, 255, 1) !important;
  border-radius: 100px;
}

.all-media {
  max-height: min(63vh, 525px);
  overflow-y: scroll;
}

.actions-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: 10px;
  margin-bottom: 5px;
  gap: 25px;
  position: relative;
}

.mobile .actions-container {
  flex-direction: row;
  margin-left: 8px;
  margin-right: 8px;
}

.upload-btn,
.paste-btn,
.choose-btn,
.ai-btn {
  width: 693.5px !important;
  max-width: 98% !important;
  height: 57px !important;
  border-radius: 15px;
  background-color: rgba(229, 244, 255, 1) !important;
  border: 2px dashed rgba(133, 202, 255, 1);
  box-shadow: 0px 5px 0px 0px rgba(133, 202, 255, 1);
}

.ai-btn {
  background: linear-gradient(99.39deg, #d973fe 0%, #4a72ff 98.23%) !important;
  color: white !important;
  border: none !important;
  box-shadow: 0px 5px 0px 0px #4a72ff !important;
}

.choose-btn {
  background-color: #82ee77 !important;
}

.mobile .upload-btn,
.mobile .paste-btn,
.mobile .choose-btn,
.mobile .ai-btn {
  width: 50% !important;
}

.upload-text {
  font-size: 18px;
  font-weight: 500;
  line-height: 26.46px;
}

.preview-container {
  height: 500px;
}

.left-btn {
  left: 10px;
}

.right-btn {
  right: 10px;
}

.media-card {
  overflow: hidden;
  border-radius: 13px;
  position: relative;
  height: 100%;
}

.left-icon {
  left: 8px;
}

.right-icon {
  right: 8px;
}

.soft-background {
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 50%;
  padding: 12px;
}

.rightProportions {
  position: absolute;
  top: -14px;
  text-align: center;
}

.storage-meter {
  margin-top: -4px;
}

.paste-hint-text {
  font-size: 11px;
  opacity: 0.55;
  font-weight: 400;
  line-height: 1.2;
  margin-top: 2px;
}
</style>
